import type { LiveConsentSegment } from './config';
import type { FetchLike } from './transport';

/**
 * The newsletter signup, and the second place a reader's own words leave the widget.
 *
 * An email address is direct personal data, so it takes the same route free text answers take: it
 * is posted to an endpoint that stores it, and no event ever carries it. There is no path in this
 * module or anywhere else in the widget that puts an address into an event, and there must never
 * be one. Nothing here logs the address either.
 *
 * The signup is a separate act from the interaction. A reader who refused storage consent can
 * still subscribe, because subscribing is its own consent given for its own purpose, and a reader
 * who ignores the step has still completed the interaction.
 */

const EVENTS_SUFFIX = '/events';

/** The longest address the standard allows, so the field and the check agree with the endpoint. */
export const MAX_EMAIL_CHARS = 254;

/**
 * Work the subscriptions endpoint out from the events one.
 *
 * The published document carries `endpoints.subscriptions` and that is what should be used. This
 * is the fallback for documents published before the field existed, which are cached, still live
 * and still have to work, and it is the same derivation `submissions.ts` makes for the same
 * reason. Returns null when the events endpoint is not shaped the way the derivation assumes,
 * which is safer than posting an address at a guessed url.
 */
export function subscriptionsUrl(eventsEndpoint: string): string | null {
  if (!eventsEndpoint.endsWith(EVENTS_SUFFIX)) return null;
  return `${eventsEndpoint.slice(0, -EVENTS_SUFFIX.length)}/subscriptions`;
}

/**
 * A check loose enough to accept the addresses people really have, strict enough to catch a typo.
 *
 * Anything more than this belongs at the endpoint: the only address that is genuinely valid is one
 * that receives mail, and a widget cannot know that. The point of checking here is that a reader
 * who left off the domain gets a line they can act on rather than a failed request.
 */
const EMAIL = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

export function looksLikeEmail(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length < 3 || trimmed.length > MAX_EMAIL_CHARS) return false;
  return EMAIL.test(trimmed);
}

/**
 * The exact wording the reader was shown, as one string.
 *
 * Sent with the signup so the record says what this reader actually agreed to, rather than what
 * the brand's settings happen to say a year later when somebody asks. That is the whole reason it
 * is stored, so an id or a hash of the wording would be no use at all.
 *
 * The widget shows and sends the same segments, so the two cannot disagree: whatever survived
 * parsing is both what is on screen and what is posted.
 */
export function consentTextOf(segments: readonly LiveConsentSegment[]): string {
  return segments.map((segment) => segment.text).join('');
}

export interface SubscriptionRequest {
  readonly interaction_id: string;
  readonly interaction_version: number;
  readonly session_id: string;
  readonly email: string;
  readonly consent_text: string;
  readonly placement_ref?: string | undefined;
}

/** What the endpoint says happened. Double opt in answers `pending`, single opt in `subscribed`. */
export type SubscriptionStatus = 'pending' | 'subscribed';

/**
 * What came back: a status, or `accepted` for a response that was taken but said nothing readable.
 *
 * The two are kept apart because the caller cannot answer them the same way. A status is the truth
 * about this reader and is what they are told; `accepted` means the widget has to fall back to
 * what the publisher configured, which is a guess and should be recognisable as one in the code.
 */
export type SubscriptionResult = SubscriptionStatus | 'accepted';

/**
 * Post the address and return what the server says happened, or null if the request failed.
 *
 * The status comes from the endpoint rather than from the document's `double_opt_in` flag, because
 * the endpoint is the thing that knows. A reader must never be told they are subscribed while a
 * confirmation email is still sitting in their inbox unopened.
 */
export async function postSubscription(
  url: string,
  fetchImpl: FetchLike,
  body: SubscriptionRequest,
): Promise<SubscriptionResult | null> {
  try {
    const response = await fetchImpl(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'omit',
      mode: 'cors',
    });
    if (!response.ok) return null;

    // A 2xx means the address was taken, whatever the body turns out to look like, so a body that
    // cannot be read is not a failure: reporting one would send a subscribed reader round again.
    const payload = (await response.json().catch(() => null)) as unknown;
    if (typeof payload !== 'object' || payload === null || Array.isArray(payload))
      return 'accepted';

    const status = (payload as Record<string, unknown>)['status'];
    if (status === 'pending' || status === 'subscribed') return status;
    return 'accepted';
  } catch {
    return null;
  }
}
